// Pure, synchronous decimal-string quantity helpers for the inventory
// track, no I/O and no persistence: the same "no floats" discipline
// src/domain/pricing.ts's own module header establishes for money and pour
// ounces, generalized to inventory quantities.
//
// Every inventory quantity in this codebase (order_line_components.quantity,
// inventory_postings.quantity_delta, and every new column this track adds)
// is NUMERIC(12,4): up to four decimal places, never a JS `number`, because
// a binary float cannot represent every decimal fraction exactly. This
// module parses a decimal string into a scaled BigInt (ten-thousandths, the
// exact precision the NUMERIC(12,4) columns carry) and formats one back,
// so a caller that needs to add or subtract two quantities (variance,
// closeCountSession's own "counted minus on-hand-at-start") does so on
// BigInt, never on a parsed float. Postgres's own NUMERIC arithmetic is
// already exact decimal (SUM() over a NUMERIC column never loses
// precision), so this module is used for two things only: validating an
// external-input decimal string BEFORE it reaches a query parameter, and
// the one JS-side subtraction closeCountSession needs to compute a
// variance value it writes back out to append-only storage. Reading an
// on-hand total is a plain COALESCE(SUM(...), 0) in SQL, per
// src/domain/inventory.ts's own module header, not reimplemented here.

import { ValidationError } from "./errors.ts";
import { normalizeLeadingDot } from "./leadingDot.ts";
import { halfUpDivide } from "./pricing.ts";

const SCALE = 10_000n; // NUMERIC(12,4): four decimal places.

const QUANTITY_PATTERN = /^-?\d+(\.\d{1,4})?$/;

/**
 * Parses a decimal string with up to four decimal places (optionally
 * negative) into a BigInt scaled by 10,000 (ten-thousandths of a unit).
 * Rejects a fifth decimal digit rather than silently truncating or
 * rounding it away, matching pricing.ts's parseOuncesToHundredths.
 */
export function parseQuantity(value: string, label = "quantity"): bigint {
  const trimmed = normalizeLeadingDot(value);
  if (!QUANTITY_PATTERN.test(trimmed)) {
    throw new ValidationError(
      `${label} must be a decimal string with at most four decimal places ` +
        `(e.g. "12.5" or "-1.25"); received "${value}"`,
    );
  }
  const negative = trimmed.startsWith("-");
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const [wholePart, fractionalPartRaw] = unsigned.split(".");
  const fractionalPart = (fractionalPartRaw ?? "").padEnd(4, "0");
  // wholePart is always defined: QUANTITY_PATTERN requires at least one
  // leading digit before any optional decimal point and after any
  // optional leading "-".
  const magnitude = BigInt(wholePart as string) * SCALE + BigInt(fractionalPart);
  return negative ? -magnitude : magnitude;
}

/** Inverse of parseQuantity: a scaled BigInt back to its minimal decimal
 * string (trailing fractional zeros trimmed, "0" for exactly zero, a
 * leading "-" only for a genuinely negative non-zero value). */
export function formatQuantity(scaled: bigint): string {
  const negative = scaled < 0n;
  const magnitude = negative ? -scaled : scaled;
  const whole = magnitude / SCALE;
  const fraction = magnitude % SCALE;
  if (fraction === 0n) {
    return `${negative ? "-" : ""}${whole}`;
  }
  const fractionStr = fraction.toString().padStart(4, "0").replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}.${fractionStr}`;
}

/** parseQuantity, additionally rejecting a negative value. Used for a
 * physical count observation and a stock receipt's own magnitude: what is
 * literally on a shelf, or literally received, is never negative, even
 * though the DERIVED on-hand and variance figures this module also helps
 * compute are explicitly allowed to go negative (P0.7: "Negative on-hand
 * remains visible and explainable rather than being silently clamped to
 * zero"). */
export function requireNonNegativeQuantity(value: string, label = "quantity"): bigint {
  const scaled = parseQuantity(value, label);
  if (scaled < 0n) {
    throw new ValidationError(`${label} must not be negative; received "${value}"`);
  }
  return scaled;
}

/** requireNonNegativeQuantity, additionally rejecting exactly zero. A
 * stock receipt raises on-hand by construction; a zero-quantity receipt
 * would be a no-op record with no depletion-side analog, so it is
 * rejected as a clear 400 rather than silently accepted as a meaningless
 * append-only row. */
export function requirePositiveQuantity(value: string, label = "quantity"): bigint {
  const scaled = requireNonNegativeQuantity(value, label);
  if (scaled === 0n) {
    throw new ValidationError(`${label} must be greater than zero; received "${value}"`);
  }
  return scaled;
}

/** requireNonNegativeQuantity, additionally rejecting a value with more
 * than two decimal places. INV37 (docs/build-briefs/INV37-count-in-bottles.md;
 * DEC-337), owner directive 2026-10-09: "we are counting bottles, not
 * ounces ... I should be able to put in a value like 1.5 bottles, 2.25
 * bottles". This SUPERSEDES the 10% increment rule INV2 built here as
 * requireBottleTenthIncrement for P0.7's "Partial bottles can be counted in
 * 10% increments or by exact ounces". recordCount calls this only when the
 * observation's unit is "bottle"; a hundredth of a unit is 100 at this
 * module's 10,000-scale (SCALE), so the check is exact-integer BigInt
 * modulo, never a float comparison. Zero is allowed and a negative value is
 * refused by requireNonNegativeQuantity's own rule. The "oz" path never
 * calls this and keeps its full four-decimal precision. */
export function requireBottleTwoDecimals(value: string, label = "quantity"): bigint {
  const scaled = requireNonNegativeQuantity(value, label);
  if (scaled % 100n !== 0n) {
    throw new ValidationError(
      `${label} must be a number of bottles with at most two decimal places ` +
        `(e.g. "1", "1.5", "2.25"); received "${value}". Use exact ounces for a finer reading.`,
    );
  }
  return scaled;
}

/** INV37. Rounds a SCALE-scaled quantity to two decimal places, half away
 * from zero, on exact BigInt arithmetic (halfUpDivide, the rule this
 * codebase already applies to money and pour ounces). Used only to build
 * display figures; nothing stored goes through it. The result is still
 * SCALE-scaled, so formatQuantity trims its trailing zeros. */
export function roundScaledToHundredths(scaled: bigint): bigint {
  return halfUpDivide(scaled, 100n) * 100n;
}

/** The exact BigInt subtraction closeCountSession uses for one product's
 * variance: counted minus on-hand-at-session-start, both already-parsed
 * scaled quantities. Exported as its own named function (rather than a
 * bare "-" at the call site) so the one subtraction this whole track's
 * money-shaped math depends on has one obvious, unit-tested home. */
export function subtractQuantity(minuendScaled: bigint, subtrahendScaled: bigint): bigint {
  return minuendScaled - subtrahendScaled;
}

/** INV14 (docs/build-briefs/INV14-container-volume-model.md). Multiplies
 * two SCALE-scaled quantities (this module's own ten-thousandths
 * convention, e.g. a container count and a keg size's volume_oz) and
 * rescales the product back down to that same SCALE, rather than leaving
 * it at SCALE^2. Reuses src/domain/pricing.ts's own halfUpDivide, the
 * identical "round half away from zero on exact BigInt arithmetic" rule
 * this codebase already applies to money and pour ounces, so a
 * container-count resolution never rounds any differently than a price
 * does. No JS `Number` is ever involved on either side of this
 * multiplication. */
export function multiplyScaledQuantities(aScaled: bigint, bScaled: bigint): bigint {
  return halfUpDivide(aScaled * bScaled, SCALE);
}

/** INV8 (docs/build-briefs/INV8-par-in-containers.md Section 2.2;
 * DEC-176). multiplyScaledQuantities' algebraic inverse: divides one
 * SCALE-scaled quantity by another and rescales the quotient back up to
 * SCALE, half-up, rather than leaving it at SCALE^0 (a bare ratio). Used
 * for converting an on-hand ounce figure into a count unit (a container's
 * own ounces-per-unit as the divisor) and for a size conversion between
 * units (src/domain/inventoryStockState.ts's convertSizeToOunces). No JS
 * `Number` is ever involved on either side of this division; halfUpDivide
 * itself refuses a non-positive denominator, so a caller with a zero or
 * negative divisor gets a thrown RangeError rather than a silently wrong
 * quotient. */
export function divideScaledQuantities(aScaled: bigint, bScaled: bigint): bigint {
  return halfUpDivide(aScaled * SCALE, bScaled);
}

/** INV18 (docs/build-briefs/INV18-keg-usable-yield.md; DEC-122). Applies
 * a tenant's configured keg usable-yield percentage (basis points, e.g.
 * 9000 for 90% usable / 10% loss) to a gross scaled quantity, returning
 * the net scaled quantity. This is the ONE place a yield factor is
 * allowed to touch a figure derived from the ledger; see the build
 * brief's own "one boundary" section. Never called from
 * recordStockReceipt or inventorySale.ts.
 *
 * This module's own SCALE (10,000, four decimal places) is numerically
 * identical to the basis-points denominator (10,000), so a raw
 * yieldBasisPoints integer is already a valid SCALE-scaled fractional
 * value (yieldBasisPoints / 10000) with no conversion step: this reuses
 * multiplyScaledQuantities unchanged, rounding half-up on exact BigInt
 * arithmetic, the same boundary this codebase already applies to money,
 * pour ounces, and container counts. */
export function applyYieldBasisPoints(grossScaled: bigint, yieldBasisPoints: number): bigint {
  return multiplyScaledQuantities(grossScaled, BigInt(yieldBasisPoints));
}

/** INV18. Converts a net scaled ounce quantity into a whole number of
 * pours of a given pour size, per INV14's own stated formula
 * ("expected_pours = floor(expected_net_oz / pour_ounces)"): a floor, not
 * a half-up rounding, because a partial pour is not a pour. BigInt's
 * native `/` already floors toward zero for two non-negative operands. */
export function expectedPourCount(netScaled: bigint, pourOunceScaled: bigint): bigint {
  if (pourOunceScaled <= 0n) {
    throw new ValidationError("expectedPourCount requires a positive pour ounce quantity");
  }
  return netScaled / pourOunceScaled;
}
